// Shared helpers for the mockups. All grouped-inset / iOS flavored.

import type { ReactNode } from "react";
import { Check, TouchButton } from "./MockShell";

export function Stepper<T extends string>({
  steps,
  current,
  onPick,
}: {
  steps: { id: T; label: string }[];
  current: T;
  onPick: (id: T) => void;
}) {
  const currentIdx = steps.findIndex((s) => s.id === current);
  const total = steps.length;
  return (
    <div className="px-4 sm:px-6 pb-4">
      <div className="flex items-center gap-1.5 mb-3">
        {steps.map((s, idx) => {
          const done = idx < currentIdx;
          const active = idx === currentIdx;
          return (
            <span
              key={s.id}
              className={`h-1 flex-1 rounded-full transition ${
                active ? "bg-[#052460]" : done ? "bg-slate-900" : "bg-slate-200"
              }`}
            />
          );
        })}
      </div>
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-slate-500">
          Step {currentIdx + 1} of {total}
        </div>
        {currentIdx > 0 && (
          <button
            type="button"
            onClick={() => {
              const prev = steps[currentIdx - 1];
              if (prev) onPick(prev.id);
            }}
            className="text-[13px] text-[#007aff]"
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
}

export function StepTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-[28px] font-bold tracking-tight px-4 sm:px-6 mb-3">{children}</h2>;
}

export function RuleRow({
  ok,
  warn,
  info,
  label,
  detail,
  action,
}: {
  ok?: boolean;
  warn?: boolean;
  info?: boolean;
  label: string;
  detail?: string;
  action?: string;
}) {
  const icon = ok ? (
    <span className="h-6 w-6 rounded-full bg-emerald-500 text-white grid place-items-center">
      <Check />
    </span>
  ) : warn ? (
    <span className="h-6 w-6 rounded-full bg-amber-500 text-white grid place-items-center text-[11px] font-bold">
      !
    </span>
  ) : info ? (
    <span className="h-6 w-6 rounded-full bg-sky-500 text-white grid place-items-center text-[11px] font-bold">
      i
    </span>
  ) : (
    <span className="h-6 w-6 rounded-full bg-slate-200" />
  );
  return (
    <div className="flex items-center gap-3 px-4 py-3 min-h-12 hairline last:shadow-none">
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-[15px] text-slate-900 truncate">{label}</div>
        {detail && (
          <div className="text-[13px] text-slate-500 whitespace-normal mt-0.5">{detail}</div>
        )}
      </div>
      {action && (
        <TouchButton variant="ghost" size="sm" mock={`Would: ${action}`}>
          {action}
        </TouchButton>
      )}
    </div>
  );
}

export function KPI({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-2xl p-4">
      <div className="text-[12px] text-slate-500 uppercase tracking-wider font-medium">{label}</div>
      <div className="text-[28px] font-semibold tracking-tight mt-0.5 tabular-nums text-slate-900">
        {value}
      </div>
      {sub && <div className="text-[12px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export function AcceptStep({ ok, label, detail }: { ok: boolean; label: string; detail?: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 min-h-12 hairline last:shadow-none">
      <span
        className={`h-6 w-6 rounded-full grid place-items-center shrink-0 ${
          ok ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
        }`}
      >
        {ok && <Check />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] text-slate-900">{label}</div>
        {detail && <div className="text-[13px] text-slate-500 mt-0.5">{detail}</div>}
      </div>
    </div>
  );
}

export function PrepRow({ label, action }: { label: string; action: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 min-h-12 hairline last:shadow-none">
      <div className="text-[15px] text-slate-900">{label}</div>
      <TouchButton variant="ghost" size="sm" mock={`Would: ${action}`}>
        {action}
      </TouchButton>
    </div>
  );
}
